import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { post_api } from '../../../../../redux/api_funcs';
import { get_api_url } from '../../../../../utils/urls';

class AddPenaltyPopup extends Component {
  state = {
    penaltyAmount: '',
    penaltyReason: '',
    errors: {},
    submitting: false,
    submitError: ''
  };

  dialogRef = createRef();

  firstInputRef = createRef();

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
    // Focus the first input on mount
    if (this.firstInputRef.current) {
      this.firstInputRef.current.focus();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown = (e) => {
    if (e.key === 'Escape' && !this.state.submitting) {
      this.props.onClose();
    }
  };

  handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !this.state.submitting) {
      this.props.onClose();
    }
  };

  validate = () => {
    const { penaltyAmount, penaltyReason } = this.state;
    const errors = {};

    // Validate amount
    const amount = parseInt(penaltyAmount, 10);
    if (!penaltyAmount || penaltyAmount.trim() === '') {
      errors.penaltyAmount = 'Amount is required';
    } else if (Number.isNaN(amount) || amount <= 0) {
      errors.penaltyAmount = 'Amount must be greater than 0';
    } else if (!Number.isInteger(amount)) {
      errors.penaltyAmount = 'Amount must be a whole number';
    }

    // Validate reason
    if (!penaltyReason || penaltyReason.trim() === '') {
      errors.penaltyReason = 'Reason is required';
    } else if (penaltyReason.trim().length < 5) {
      errors.penaltyReason = 'Reason must be at least 5 characters';
    } else if (penaltyReason.trim().length > 200) {
      errors.penaltyReason = 'Reason must not exceed 200 characters';
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSubmit = (e) => {
    e.preventDefault();

    if (!this.validate()) return;

    const { sessionUuid, onClose, onSuccess } = this.props;
    const { penaltyAmount, penaltyReason } = this.state;

    this.setState({ submitting: true, submitError: '' });

    const url = get_api_url(
      '/v2/cms_user/teacher-payments/sessions/add_payout_penalty',
      null,
      true
    );
    const payload = {
      session_uuid: sessionUuid,
      penalty_amount: parseInt(penaltyAmount, 10),
      penalty_reason: penaltyReason.trim()
    };

    post_api(url, payload, true)
      .then(() => {
        this.setState({ submitting: false });
        if (onSuccess) onSuccess();
        onClose();
      })
      .catch((error) => {
        console.error('Error adding penalty:', error);
        this.setState({
          submitting: false,
          submitError: error.response?.data?.message || 'Failed to add penalty. Please try again.'
        });
      });
  };

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      [name]: value,
      errors: { ...prevState.errors, [name]: '' }
    }));
  };

  render() {
    const { onClose } = this.props;
    const { penaltyAmount, penaltyReason, errors, submitting, submitError } = this.state;

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={this.handleBackdropClick}
        role="presentation"
      >
        <div
          ref={this.dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-penalty-title"
          className="bg-white rounded-lg shadow-xl w-full max-w-md focus:outline-none"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 id="add-penalty-title" className="text-lg font-semibold text-gray-900">
              Add Penalty
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={submitting}
              aria-label="Close dialog"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={this.handleSubmit} className="p-4" noValidate>
            {submitError && (
              <div
                className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
                role="alert"
              >
                {submitError}
              </div>
            )}

            <div className="mb-4">
              <label
                htmlFor="penaltyAmount"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Penalty Amount{' '}
                <span className="text-red-500" aria-hidden="true">
                  *
                </span>
                <span className="sr-only">(required)</span>
              </label>
              <input
                ref={this.firstInputRef}
                type="number"
                id="penaltyAmount"
                name="penaltyAmount"
                value={penaltyAmount}
                onChange={this.handleInputChange}
                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.penaltyAmount ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter amount"
                min="1"
                step="1"
                disabled={submitting}
                aria-invalid={!!errors.penaltyAmount}
                aria-describedby={errors.penaltyAmount ? 'penaltyAmount-error' : undefined}
              />
              {errors.penaltyAmount && (
                <p id="penaltyAmount-error" className="mt-1 text-sm text-red-600" role="alert">
                  {errors.penaltyAmount}
                </p>
              )}
            </div>

            <div className="mb-4">
              <label
                htmlFor="penaltyReason"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Reason{' '}
                <span className="text-red-500" aria-hidden="true">
                  *
                </span>
                <span className="sr-only">(required)</span>
                <span className="text-gray-400 font-normal ml-1">(5-200 characters)</span>
              </label>
              <textarea
                id="penaltyReason"
                name="penaltyReason"
                value={penaltyReason}
                onChange={this.handleInputChange}
                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                  errors.penaltyReason ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter reason for penalty"
                rows={3}
                maxLength={200}
                disabled={submitting}
                aria-invalid={!!errors.penaltyReason}
                aria-describedby="penaltyReason-count penaltyReason-error"
              />
              <div className="flex justify-between mt-1">
                {errors.penaltyReason ? (
                  <p id="penaltyReason-error" className="text-sm text-red-600" role="alert">
                    {errors.penaltyReason}
                  </p>
                ) : (
                  <span />
                )}
                <span id="penaltyReason-count" className="text-xs text-gray-400" aria-live="polite">
                  {penaltyReason.length}/200
                </span>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                disabled={submitting}
                aria-busy={submitting}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Adding...
                  </span>
                ) : (
                  'Add Penalty'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
}

AddPenaltyPopup.propTypes = {
  sessionUuid: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
};

AddPenaltyPopup.defaultProps = {
  onSuccess: null
};

export default AddPenaltyPopup;
